import { AsYouType, CountryCode, getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'

export const DEFAULT_COUNTRY: CountryCode = 'PL'
export const DEFAULT_PREFIX = '+48'

/** Kraje wyświetlane na górze listy */
export const PINNED_COUNTRIES: CountryCode[] = ['PL', 'DE', 'GB', 'UA', 'CZ', 'SK', 'LT']

/** Kraj wybierany domyślnie dla prefiksów współdzielonych przez kilka krajów */
const MAIN_COUNTRY_FOR_PREFIX: Record<string, CountryCode> = {
  '1': 'US',
  '7': 'RU',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
}

export interface PhoneCountry {
  iso: CountryCode
  prefix: string
  name: string
}

export interface PhoneValue {
  prefix: string
  phone: string
}

export const onlyDigits = (value = '') => value.replace(/\D/g, '')

/** "48" | "+48" | "0048" -> "+48" */
export const normalizePrefix = (prefix?: string | number | null) => {
  const digits = onlyDigits(String(prefix ?? '')).replace(/^00/, '')
  return digits ? `+${digits}` : ''
}

export const getCountryPrefix = (iso: CountryCode) => `+${getCountryCallingCode(iso)}`

export const getCountryByPrefix = (prefix?: string, current?: CountryCode): CountryCode | undefined => {
  const code = onlyDigits(normalizePrefix(prefix))
  if (!code) return undefined
  if (current && getCountryCallingCode(current) === code) return current
  if (MAIN_COUNTRY_FOR_PREFIX[code]) return MAIN_COUNTRY_FOR_PREFIX[code]
  return getCountries().find(iso => getCountryCallingCode(iso) === code)
}

const displayNamesCache: Record<string, Intl.DisplayNames | null> = {}
const getCountryName = (iso: CountryCode, lang: string) => {
  if (!(lang in displayNamesCache)) {
    try {
      displayNamesCache[lang] = new Intl.DisplayNames([lang], { type: 'region' })
    } catch {
      displayNamesCache[lang] = null
    }
  }
  try {
    return displayNamesCache[lang]?.of(iso) || iso
  } catch {
    return iso
  }
}

const countriesCache: Record<string, PhoneCountry[]> = {}
export const getPhoneCountries = (lang = 'pl'): PhoneCountry[] => {
  if (!countriesCache[lang]) {
    const collator = new Intl.Collator(lang)
    const all = getCountries()
      .map(iso => ({ iso, prefix: getCountryPrefix(iso), name: getCountryName(iso, lang) }))
      .sort((a, b) => collator.compare(a.name, b.name))

    const pinned = PINNED_COUNTRIES
      .map(iso => all.find(c => c.iso === iso))
      .filter((c): c is PhoneCountry => !!c)

    countriesCache[lang] = [...pinned, ...all.filter(c => !PINNED_COUNTRIES.includes(c.iso))]
  }
  return countriesCache[lang]
}

/**
 * Rozdziela numer na prefiks i numer krajowy.
 * Obsługuje stare dane, w których prefiks był zapisany razem z numerem ("+48531876282", "0048...").
 */
export const getPhoneParts = (phone?: string | null, prefix?: string | null): PhoneValue => {
  const normalizedPrefix = normalizePrefix(prefix)
  const raw = (phone || '').trim()

  if (!normalizedPrefix && /^(\+|00)/.test(raw)) {
    const parsed = parsePhoneNumberFromString(raw.replace(/^00/, '+'))
    if (parsed) return { prefix: `+${parsed.countryCallingCode}`, phone: parsed.nationalNumber }
  }

  return {
    prefix: normalizedPrefix || DEFAULT_PREFIX,
    phone: onlyDigits(raw),
  }
}

/** Numer wpisany/wklejony w formacie międzynarodowym -> prefiks + numer */
export const parseInternational = (value: string): PhoneValue | undefined => {
  const raw = value.trim()
  if (!/^(\+|00)/.test(raw)) return undefined
  const parsed = parsePhoneNumberFromString(raw.replace(/^00/, '+'))
  return parsed ? { prefix: `+${parsed.countryCallingCode}`, phone: parsed.nationalNumber } : undefined
}

export const isValidPhone = (prefix?: string, phone?: string) => {
  const code = normalizePrefix(prefix)
  const digits = onlyDigits(phone)
  if (!code || !digits) return false
  try {
    return isValidPhoneNumber(`${code}${digits}`)
  } catch {
    return false
  }
}

/**
 * Numery testowe udostępniane klientom. Nie są poprawne wg libphonenumber,
 * więc AsYouType ich nie formatuje — maskujemy je ręcznie (grupy po 3 cyfry).
 */
const TEST_PHONES: Partial<Record<CountryCode, string[]>> = {
  PL: ['999999999'],
}

const groupBy3 = (digits: string) => digits.replace(/(\d{3})(?=\d)/g, '$1 ')

/** Czy wpisywane cyfry to (początek) numeru testowego */
const isTestPhoneInput = (digits: string, country?: CountryCode) =>
  !!digits && !!country && !!TEST_PHONES[country]?.some(test => test.startsWith(digits))

/** Formatowanie numeru krajowego w trakcie wpisywania, np. "531876282" -> "531 876 282" */
export const formatNationalPhone = (phone: string, country?: CountryCode) => {
  const digits = onlyDigits(phone)
  if (!digits) return ''
  if (isTestPhoneInput(digits, country)) return groupBy3(digits)
  return new AsYouType(country).input(digits)
}

/** Numer do wyświetlenia, np. "+48 531 876 282" */
export const formatPhone = (phone?: string | null, prefix?: string | null) => {
  if (!phone) return phone || ''
  const parts = getPhoneParts(phone, prefix)
  const country = getCountryByPrefix(parts.prefix)
  if (country && TEST_PHONES[country]?.includes(parts.phone)) return `${parts.prefix} ${groupBy3(parts.phone)}`
  const parsed = parsePhoneNumberFromString(`${parts.prefix}${parts.phone}`)
  return parsed ? parsed.formatInternational() : `${parts.prefix} ${parts.phone}`
}
